import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  dataTableSx,
  HairlineCard,
  IconButton,
  Icons,
  iconSize,
  MenuItem,
  palette,
  Stack,
  StatusChip,
  statusTone,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  fontStacks,
  useSnackbar,
} from '@munifocus/ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScope } from '../providers/ScopeProvider';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import type {
  FileFilters,
  FilePriority,
  FileStage,
  FileSummary,
  SavedSearch,
} from '@munifocus/contracts-gov';
import {
  BUILTIN_SMART_LIST_FILTERS,
  FILE_PRIORITIES,
  FILE_STAGES,
  parseSmartListKey,
  sanitizeFileFilters,
} from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { UpcomingDeadlines } from '../components/UpcomingDeadlines';
import { InspectionFreshness } from '../components/InspectionFreshness';
import { useFiles } from '../lib/files-api';
import { statusChipKind, stageLabelKey, statusLabelKey } from '../lib/file-display';
import { timeAgo } from '../lib/relative-time';
import { useDeleteSavedSearch, useFileQuery, useSmartLists } from '../lib/productivity-api';
import { useConfirmAction } from '../lib/useConfirmAction';
import { apiErrorMessage } from '../lib/mutation-toast';
import { SmartListStrip } from '../components/dashboard/SmartListStrip';
import { SaveSearchDialog } from '../components/dashboard/SaveSearchDialog';
import { BulkActionBar } from '../components/dashboard/BulkActionBar';
import { FileTags } from '../components/FileTags';
import { frenchFirstOfMonth } from '@munifocus/utils';

type FilterKey = 'all' | 'active' | 'review' | 'paused';

const FILTERS: { key: FilterKey; labelKey: string; fallback: string }[] = [
  { key: 'all', labelKey: 'gov.dashboard.filter.all', fallback: 'All statuses' },
  { key: 'active', labelKey: 'gov.dashboard.filter.active', fallback: 'Active' },
  { key: 'review', labelKey: 'gov.dashboard.filter.review', fallback: 'In review' },
  { key: 'paused', labelKey: 'gov.dashboard.filter.paused', fallback: 'Paused' },
];

function matchesFilter(f: FileSummary, filter: FilterKey): boolean {
  switch (filter) {
    case 'active':
      return f.status === 'active';
    case 'review':
      return f.stage === 'review';
    case 'paused':
      return f.status === 'paused' || f.status === 'awaiting_external';
    case 'all':
    default:
      return true;
  }
}

/**
 * The dashboard's narrowing controls as a saved-search filter, layered over the
 * smart list it was refined from — so "My overdue, urgent only" saves as both.
 */
function toSavedFilters(
  base: FileFilters,
  {
    query,
    filter,
    stage,
    priority,
  }: { query: string; filter: FilterKey; stage: FileStage | 'all'; priority: FilePriority | 'all' },
): FileFilters {
  const out: FileFilters = { ...base };
  if (query.trim()) out.q = query.trim();
  if (stage !== 'all') out.stages = [stage];
  if (priority !== 'all') out.priorities = [priority];
  if (filter === 'active') out.statuses = ['active'];
  if (filter === 'paused') out.statuses = ['paused', 'awaiting_external'];
  if (filter === 'review') out.stages = ['review'];
  return sanitizeFileFilters(out);
}

/**
 * Mobile-only floating action button for "New file" — sits above the bottom tab
 * bar (64px) and replaces the header action on small screens, per the design handoff.
 */
function NewFileFab({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={label}
      sx={{
        display: { xs: 'flex', md: 'none' },
        position: 'fixed',
        right: 16,
        bottom: 'calc(64px + 16px + env(safe-area-inset-bottom, 0px))',
        zIndex: 1200,
        width: 56,
        height: 56,
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        alignItems: 'center',
        justifyContent: 'center',
        color: palette.brand.rail,
        backgroundColor: palette.brand.accentLight,
        boxShadow: `0 6px 20px ${palette.overlay.cardShadow}`,
        transition: 'transform 200ms, background-color 200ms',
        '&:hover': { backgroundColor: palette.brand.accentLightHover },
        '&:active': { transform: 'scale(0.98)' },
      }}
    >
      <Icons.Plus size={iconSize.xl} />
    </Box>
  );
}

/** One headline number in the dashboard summary strip. */
function StatCard({ value, label }: { value: number; label: string }) {
  return (
    <HairlineCard sx={{ p: 2.5, flex: 1 }}>
      <Typography
        sx={{ fontFamily: fontStacks.display, fontSize: 30, fontWeight: 700, lineHeight: 1 }}
      >
        {value}
      </Typography>
      <Typography sx={{ mt: 0.75, fontSize: 13, color: palette.text.secondary }}>
        {label}
      </Typography>
    </HairlineCard>
  );
}

/** Columns the officer can order the queue by. */
type SortKey =
  | 'reference'
  | 'address'
  | 'stage'
  | 'status'
  | 'priority'
  | 'requests'
  | 'createdAt'
  | 'updatedAt';

interface Column {
  key: SortKey;
  labelKey: string;
  /** Right-aligned, and never the widest thing in the row. */
  numeric?: boolean;
}

const COLUMNS: Column[] = [
  { key: 'reference', labelKey: 'gov.file.number' },
  { key: 'address', labelKey: 'gov.file.address' },
  { key: 'stage', labelKey: 'gov.file.stage' },
  { key: 'status', labelKey: 'gov.dashboard.column.status' },
  { key: 'priority', labelKey: 'gov.file.priority' },
  // Owner document requests awaiting a decision. Sortable, because the reason
  // to have the column at all is to find the files that are waiting on you.
  { key: 'requests', labelKey: 'gov.documentRequests.column', numeric: true },
  { key: 'createdAt', labelKey: 'gov.dashboard.column.created', numeric: true },
  { key: 'updatedAt', labelKey: 'gov.file.lastActivity', numeric: true },
];

/** Rank for the two coded columns, so sorting them follows the workflow. */
const STAGE_RANK: Record<string, number> = Object.fromEntries(FILE_STAGES.map((s, i) => [s, i]));
const PRIORITY_RANK: Record<string, number> = Object.fromEntries(
  FILE_PRIORITIES.map((p, i) => [p, i]),
);

function sortValue(f: FileSummary, key: SortKey): string | number {
  switch (key) {
    case 'stage':
      return STAGE_RANK[f.stage] ?? 0;
    case 'priority':
      return PRIORITY_RANK[f.priority] ?? 0;
    case 'requests':
      return f.openDocumentRequests ?? 0;
    case 'createdAt':
    case 'updatedAt':
      return Date.parse(f[key]);
    default:
      return f[key].toLowerCase();
  }
}

/** An absolute stamp over the relative one — "3 days ago" is no use in a log. */
function Stamp({ iso, locale, justNow }: { iso: string; locale: string; justNow: string }) {
  const d = new Date(iso);
  return (
    <>
      <Typography sx={{ fontSize: 13, color: palette.text.primary, whiteSpace: 'nowrap' }}>
        {frenchFirstOfMonth(
          d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: '2-digit' }),
        )}
        {', '}
        {d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
      </Typography>
      <Typography sx={{ fontSize: 12, color: palette.text.muted, whiteSpace: 'nowrap' }}>
        {timeAgo(iso, locale, justNow)}
      </Typography>
    </>
  );
}

/**
 * The queue as a data table: every file one row, every column sortable, the
 * filters above it narrowing what is in it.
 *
 * Desktop only — seven columns do not survive a phone. Below `md` the same rows
 * render as a stacked list further down.
 */
function FilesTable({
  files,
  sort,
  onSort,
  onOpen,
  selected,
  onToggle,
  onToggleAll,
}: {
  files: FileSummary[];
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  onSort: (key: SortKey) => void;
  onOpen: (id: string) => void;
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onToggleAll: (ids: string[], on: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const justNow = t('gov.dashboard.justNow');

  return (
    // Not on `DataTable`: this table and the phone card list below it render
    // the same page of the same caseload, and share one sort. Handing the sort
    // to the table would leave the card list unsorted. It takes the shared look
    // so the two still read as one system.
    <HairlineCard sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 900, ...dataTableSx }}>
        <TableHead>
          <TableRow>
            {/* Selection for the bulk actions: the page on screen, not the
                whole caseload — a select-all that reached past what the
                officer can see would act on files they never looked at. */}
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={files.length > 0 && files.every((f) => selected.has(f.id))}
                indeterminate={
                  files.some((f) => selected.has(f.id)) && !files.every((f) => selected.has(f.id))
                }
                onChange={(e) =>
                  onToggleAll(
                    files.map((f) => f.id),
                    e.target.checked,
                  )
                }
                slotProps={{ input: { 'aria-label': t('gov.bulk.selectPage') } }}
              />
            </TableCell>
            {COLUMNS.map((col) => (
              <TableCell
                key={col.key}
                align={col.numeric ? 'right' : 'left'}
                sortDirection={sort.key === col.key ? sort.dir : false}
                sx={{ whiteSpace: 'nowrap' }}
              >
                <TableSortLabel
                  active={sort.key === col.key}
                  direction={sort.key === col.key ? sort.dir : 'asc'}
                  onClick={() => onSort(col.key)}
                >
                  {t(col.labelKey)}
                </TableSortLabel>
              </TableCell>
            ))}
            {/* Header-less action column: the label would say "Open" seven times. */}
            <TableCell sx={{ width: 44 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((f) => (
            <TableRow
              key={f.id}
              hover
              onClick={() => onOpen(f.id)}
              selected={selected.has(f.id)}
              sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
            >
              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  size="small"
                  checked={selected.has(f.id)}
                  onChange={() => onToggle(f.id)}
                  slotProps={{
                    input: { 'aria-label': t('gov.bulk.selectFile', { reference: f.reference }) },
                  }}
                />
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                  {/* The row's `onClick` is a mouse convenience; on its own it
                      made opening a file — the primary action of the whole
                      portal — unreachable from the keyboard (WCAG 2.1.1). The
                      reference is a real link, so the row is focusable, and
                      middle-click and "open in new tab" work again. It carries
                      the address as well: "GOV-014" alone does not say which
                      file it opens when read out of the row's context. */}
                  <Typography
                    component={RouterLink}
                    to={`/files/${f.id}`}
                    aria-label={`${f.reference} — ${f.address}`}
                    sx={{
                      fontFamily: fontStacks.mono,
                      fontSize: 13,
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      color: 'inherit',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {f.reference}
                  </Typography>
                  {/* s.144(9) outranks every other signal in the row. */}
                  {f.emergency && (
                    <Tooltip title={t('gov.status.emergency')}>
                      <Icons.Zap size={iconSize.sm} color={palette.semantic.emergency} />
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
              <TableCell sx={{ color: palette.text.primary, minWidth: 220 }}>
                {f.address}
                {f.tags && f.tags.length > 0 && <FileTags tags={f.tags} sx={{ mt: 0.5 }} />}
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{t(stageLabelKey(f.stage))}</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                <StatusChip status={statusChipKind(f.status)} label={t(statusLabelKey(f.status))} />
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {/* Routine is the resting state — a column of "Routine" chips
                    would drown the two levels that mean something. */}
                {f.priority === 'routine' ? (
                  <Typography sx={{ fontSize: 13, color: palette.text.muted }}>
                    {t('gov.priority.routine')}
                  </Typography>
                ) : (
                  <StatusChip
                    status={f.priority === 'urgent' ? 'warning' : 'pending'}
                    label={t(`gov.priority.${f.priority}`)}
                  />
                )}
              </TableCell>
              <TableCell align="right">
                {/* Only a file with something outstanding shows a number. A
                    column of zeroes is noise in a queue you scan by eye. */}
                {f.openDocumentRequests ? (
                  <StatusChip
                    status={statusTone.documentRequest.requested}
                    label={String(f.openDocumentRequests)}
                    aria-label={t('gov.documentRequests.columnAria', {
                      count: f.openDocumentRequests,
                    })}
                  />
                ) : null}
              </TableCell>
              <TableCell align="right">
                <Stamp iso={f.createdAt} locale={i18n.language} justNow={justNow} />
              </TableCell>
              <TableCell align="right">
                <Stamp iso={f.updatedAt} locale={i18n.language} justNow={justNow} />
              </TableCell>
              <TableCell align="right" sx={{ color: palette.text.muted }}>
                <Icons.ChevronRight size={iconSize.md} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </HairlineCard>
  );
}

/** The same rows stacked, for phones. Not cards — one tappable line per file. */
function FilesList({
  files,
  selecting,
  selected,
  onToggle,
}: {
  files: FileSummary[];
  /** Select mode: each row gets a checkbox beside its link. */
  selecting: boolean;
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();

  return (
    <HairlineCard>
      {files.map((f, idx) => (
        <Stack
          key={f.id}
          direction="row"
          sx={{
            alignItems: 'center',
            borderTop: idx === 0 ? 'none' : `1px solid ${palette.border.line}`,
          }}
        >
          {selecting && (
            <Checkbox
              checked={selected.has(f.id)}
              onChange={() => onToggle(f.id)}
              sx={{ ml: 0.5 }}
              slotProps={{
                input: { 'aria-label': t('gov.bulk.selectFile', { reference: f.reference }) },
              }}
            />
          )}
          {/* A `Box` with an `onClick` was tappable but not focusable, so on a
              phone-sized viewport the file list was mouse-only (WCAG 2.1.1). */}
          <Box
            component={RouterLink}
            to={`/files/${f.id}`}
            aria-label={`${f.reference} — ${f.address}`}
            sx={{
              display: 'block',
              flexGrow: 1,
              minWidth: 0,
              color: 'inherit',
              textDecoration: 'none',
              p: 2,
              pl: selecting ? 0.5 : 2,
              cursor: 'pointer',
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography
                sx={{
                  fontFamily: fontStacks.mono,
                  fontSize: 13,
                  fontWeight: 600,
                  color: palette.text.secondary,
                }}
              >
                {f.reference}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                {f.priority !== 'routine' && (
                  <StatusChip
                    status={f.priority === 'urgent' ? 'warning' : 'pending'}
                    label={t(`gov.priority.${f.priority}`)}
                  />
                )}
                <StatusChip status={statusChipKind(f.status)} label={t(stageLabelKey(f.stage))} />
              </Stack>
            </Stack>
            <Typography sx={{ fontSize: 15, mt: 0.5 }}>{f.address}</Typography>
            {f.tags && f.tags.length > 0 && <FileTags tags={f.tags} sx={{ mt: 0.5 }} />}
            <Typography sx={{ fontSize: 12, color: palette.text.muted, mt: 0.25 }}>
              {timeAgo(f.updatedAt, i18n.language, t('gov.dashboard.justNow'))}
            </Typography>
          </Box>
        </Stack>
      ))}
    </HairlineCard>
  );
}

export function Dashboard() {
  const { t } = useTranslation();
  const { can } = useScope();
  const navigate = useNavigate();
  // The coordinator's file queue — the one view §F's priority ordering is for.
  const caseload = useFiles('priority');

  // A smart list replaces the caseload with the server's answer for it; the
  // filters below still narrow that answer, and paging and sorting are shared.
  const [smartList, setSmartList] = useState<string | null>(null);
  const smartLists = useSmartLists();
  const listQuery = useFileQuery({ list: smartList });
  const source = smartList ? listQuery : caseload;
  const { data, isLoading, isError, refetch } = source;
  const files = useMemo(() => data?.items ?? [], [data]);
  const allFiles = caseload.data?.items;

  const snackbar = useSnackbar();
  const confirm = useConfirmAction();
  const deleteSearch = useDeleteSavedSearch();
  const [saveOpen, setSaveOpen] = useState(false);
  const [renaming, setRenaming] = useState<SavedSearch | null>(null);

  // Bulk selection, by id so it survives re-sorting and refetches.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [selecting, setSelecting] = useState(false);
  const toggle = useCallback(
    (id: string) =>
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    [],
  );
  const toggleAll = useCallback(
    (ids: string[], on: boolean) =>
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) {
          if (on) next.add(id);
          else next.delete(id);
        }
        return next;
      }),
    [],
  );
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelecting(false);
  }, []);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [page, setPage] = useState(0);
  const [stage, setStage] = useState<FileStage | 'all'>('all');
  const [priority, setPriority] = useState<FilePriority | 'all'>('all');
  // Most-recently-touched first: the queue's default question is "what moved".
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'updatedAt',
    dir: 'desc',
  });

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : // Dates open newest-first, text opens A–Z: each column's useful end.
          { key, dir: key === 'createdAt' || key === 'updatedAt' ? 'desc' : 'asc' },
    );

  // Always the whole caseload — a selected smart list narrows the table, not
  // the headline numbers.
  const stats = useMemo(() => {
    const all = allFiles ?? [];
    return {
      open: all.filter((f) => f.status !== 'closed').length,
      review: all.filter((f) => f.stage === 'review').length,
      urgent: all.filter((f) => f.priority === 'urgent' && f.status !== 'closed').length,
    };
  }, [allFiles]);

  const filtersActive =
    query.trim() !== '' || filter !== 'all' || stage !== 'all' || priority !== 'all';

  // What "Save search" would store: the selected list's criteria plus the
  // narrowing on screen.
  const parsedList = parseSmartListKey(smartList);
  const baseFilters: FileFilters =
    parsedList?.kind === 'builtin'
      ? BUILTIN_SMART_LIST_FILTERS[parsedList.builtin]
      : parsedList?.kind === 'saved'
        ? (smartLists.data?.find((l) => l.key === smartList)?.savedSearch?.filters ?? {})
        : {};
  const savedFilters = toSavedFilters(baseFilters, { query, filter, stage, priority });

  const onDeleteSearch = async (search: SavedSearch) => {
    const ok = await confirm({
      title: t('gov.smartLists.deleteTitle'),
      body: t('gov.smartLists.deleteBody', { name: search.name }),
      confirmLabel: t('common.actions.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    deleteSearch.mutate(search.id, {
      onSuccess: () => {
        if (smartList?.endsWith(search.id)) setSmartList(null);
        snackbar.success(t('gov.smartLists.deleted'));
      },
      onError: (err) => snackbar.error(apiErrorMessage(err, t('gov.smartLists.saveError'))),
    });
  };
  const clearFilters = () => {
    setQuery('');
    setFilter('all');
    setStage('all');
    setPriority('all');
    setPage(0);
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = files.filter((f) => {
      if (!matchesFilter(f, filter)) return false;
      if (stage !== 'all' && f.stage !== stage) return false;
      if (priority !== 'all' && f.priority !== priority) return false;
      if (!q) return true;
      return f.reference.toLowerCase().includes(q) || f.address.toLowerCase().includes(q);
    });
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  }, [files, filter, priority, query, sort, stage]);

  /**
   * The caseload is paged at 25.
   *
   * An officer with two hundred open files was being handed all two hundred in
   * one scroll, on a phone as well as a desktop. The filters narrow *what* is in
   * the list; paging bounds how much of it arrives at once.
   */
  const PAGE_SIZE = 25;
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  /**
   * Whether this account may start an enforcement file at all.
   *
   * `file:create` belongs to an appointment and to the municipal seats, not to
   * employment: a contractor's newly hired officer holds `company_officer` and
   * nothing else until a council appoints them. The button was rendered
   * unconditionally, so they were invited to begin something the API would have
   * refused — and the refusal would have read as a bug rather than as the
   * statutory fact it is.
   */
  const canCreateFile = can('file:create');

  // Changing a filter can leave the current page past the end of the results.
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = visible.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  // On desktop the action lives in the header; on mobile it collapses to a
  // floating action button so the cramped top bar keeps room for the account menu.
  const newFileButton = !canCreateFile ? null : (
    <Box component="span" sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
      <Button
        variant="contained"
        onClick={() => navigate('/files/new')}
        startIcon={<Icons.Plus size={iconSize.lg} />}
        sx={{
          backgroundColor: palette.brand.accentLight,
          color: palette.brand.rail,
          '&:hover': { backgroundColor: palette.brand.accentLightHover },
        }}
      >
        {t('common.actions.newFile')}
      </Button>
    </Box>
  );

  // The summary strip and the empty state speak for the whole caseload; a smart
  // list that matched nothing is a "no matches", not "you have no files".
  const hasFiles = smartList
    ? !caseload.isLoading && (allFiles?.length ?? 0) > 0
    : !isLoading && !isError && files.length > 0;
  const selectedFiles = useMemo(() => {
    const byId = new Map([...(allFiles ?? []), ...files].map((f) => [f.id, f]));
    return [...selectedIds].map((id) => byId.get(id)).filter((f): f is FileSummary => !!f);
  }, [allFiles, files, selectedIds]);

  const showEmptyState = !smartList && !isLoading && !isError && files.length === 0;

  return (
    // One New file at a time: the empty state carries its own, so the top
    // bar's comes back only once there is a list to add to.
    <AppShell actions={showEmptyState ? undefined : newFileButton}>
      {/* Not while the empty state is up: it carries its own New file button,
          and two of the same action on one screen reads as two actions. */}
      {canCreateFile && !showEmptyState && (
        <NewFileFab onClick={() => navigate('/files/new')} label={t('common.actions.newFile')} />
      )}
      {/* The page title lives in the topbar; on mobile the topbar has no room for
          it, so it repeats here below `md`. */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: { md: 'none' }, letterSpacing: '-0.01em' }}>
          {t('gov.dashboard.title')}
        </Typography>
        {hasFiles && (
          <Typography sx={{ mt: 0.5, color: palette.text.secondary }}>
            {t('gov.dashboard.count', { count: data?.total ?? files.length })}
          </Typography>
        )}
      </Box>

      {/* The list is the whole caseload up to the API's cap. Past it, say so —
          a queue that quietly stops is worse than one that admits it does. */}
      {hasFiles && data && data.total > files.length && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('gov.dashboard.truncated', { shown: files.length, total: data.total })}
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {t('common.actions.retry')}
            </Button>
          }
        >
          {t('gov.dashboard.loadError')}
        </Alert>
      )}

      {showEmptyState && (
        <HairlineCard
          sx={{
            py: 8,
            px: 3,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: palette.brand.accentTint,
            }}
          >
            <Icons.ClipboardList size={iconSize.xl} color={palette.brand.accent} />
          </Box>
          <Typography sx={{ color: palette.text.secondary, maxWidth: 320 }}>
            {/* An empty queue means two different things. An officer has
                nothing assigned yet and can start something; a contractor's
                employee who holds no appointment has nothing *and* no way to
                begin — telling them to create a file is an instruction they
                cannot follow. */}
            {canCreateFile ? t('gov.dashboard.empty') : t('gov.dashboard.emptyNoAuthority')}
          </Typography>
          {canCreateFile && (
            <Button
              variant="contained"
              startIcon={<Icons.Plus size={iconSize.lg} />}
              onClick={() => navigate('/files/new')}
            >
              {t('common.actions.newFile')}
            </Button>
          )}
        </HairlineCard>
      )}

      {hasFiles && (
        <>
          {/* Summary strip */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
            <StatCard
              value={stats.open}
              label={t('gov.dashboard.stat.open', { defaultValue: 'Open files' })}
            />
            <StatCard
              value={stats.review}
              label={t('gov.dashboard.stat.review', { defaultValue: 'In review' })}
            />
            <StatCard
              value={stats.urgent}
              label={t('gov.dashboard.stat.urgent', { defaultValue: 'Urgent' })}
            />
          </Stack>

          {/* Statutory clocks + inspection freshness across the caseload (X4) */}
          {/* Same three columns as the summary strip above, with the deadlines
              card spanning two — so both rows share one grid and their card
              edges line up rather than landing at arbitrary widths. The grid
              stretches its items (no `alignItems: start`), so the two cards end
              at one shared bottom edge however much content each holds. */}
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              mb: 3,
              gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
            <Box sx={{ gridColumn: { lg: 'span 2' }, minWidth: 0, display: 'flex' }}>
              <UpcomingDeadlines />
            </Box>
            <InspectionFreshness />
          </Box>

          {/* Smart lists: the built-ins and the saved searches, with counts. */}
          <SmartListStrip
            lists={smartLists.data ?? []}
            selected={smartList}
            onSelect={(key) => {
              setSmartList(key);
              setPage(0);
            }}
            onRename={(search) => setRenaming(search)}
            onDelete={(search) => void onDeleteSearch(search)}
          />

          {/* Filter bar. Search and the three narrowing controls sit on one row
              at `lg`; the status segments wrap under them below that. */}
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1.5}
            sx={{ mb: 2, alignItems: { lg: 'center' }, justifyContent: 'space-between' }}
          >
            <TextField
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              placeholder={t('gov.dashboard.searchPlaceholder', {
                defaultValue: 'Search by file number or address',
              })}
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <Icons.Search
                      size={iconSize.md}
                      color={palette.text.muted}
                      style={{ marginRight: 8 }}
                    />
                  ),
                },
              }}
              // Flexes with the row rather than a fixed 320px: the French
              // placeholder is half again as long as the English and was cut off.
              sx={{
                width: { xs: '100%', lg: 'auto' },
                flex: { lg: '1 1 360px' },
                minWidth: { lg: 360 },
                maxWidth: { lg: 460 },
                backgroundColor: palette.background.paper,
              }}
            />

            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                select
                size="small"
                label={t('gov.file.stage')}
                value={stage}
                onChange={(e) => {
                  setStage(e.target.value as FileStage | 'all');
                  setPage(0);
                }}
                sx={{ minWidth: 160, backgroundColor: palette.background.paper }}
              >
                <MenuItem value="all">{t('gov.dashboard.filter.allStages')}</MenuItem>
                {FILE_STAGES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(stageLabelKey(s))}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label={t('gov.file.priority')}
                value={priority}
                onChange={(e) => {
                  setPriority(e.target.value as FilePriority | 'all');
                  setPage(0);
                }}
                sx={{ minWidth: 150, backgroundColor: palette.background.paper }}
              >
                <MenuItem value="all">{t('gov.dashboard.filter.allPriorities')}</MenuItem>
                {FILE_PRIORITIES.map((p) => (
                  <MenuItem key={p} value={p}>
                    {t(`gov.priority.${p}`)}
                  </MenuItem>
                ))}
              </TextField>

              {/* A select, like the other two: three filters that all narrow the
                  same list should look and behave the same way, and segmented
                  buttons grow unusable the moment a fourth status appears. */}
              <TextField
                select
                size="small"
                label={t('gov.dashboard.filter.label')}
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterKey)}
                sx={{ minWidth: 160, backgroundColor: palette.background.paper }}
              >
                {FILTERS.map((f) => (
                  <MenuItem key={f.key} value={f.key}>
                    {t(f.labelKey, { defaultValue: f.fallback })}
                  </MenuItem>
                ))}
              </TextField>

              {filtersActive && (
                <Button size="small" onClick={clearFilters}>
                  {t('common.actions.clear')}
                </Button>
              )}
              {(filtersActive || smartList) && (
                <Button
                  size="small"
                  startIcon={<Icons.Bookmark size={iconSize.sm} />}
                  onClick={() => setSaveOpen(true)}
                >
                  {t('gov.smartLists.saveAction')}
                </Button>
              )}
              {/* Phones have no checkbox column; selecting is a mode there. */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Button
                  size="small"
                  onClick={() => (selecting ? clearSelection() : setSelecting(true))}
                >
                  {selecting ? t('gov.bulk.doneSelecting') : t('gov.bulk.select')}
                </Button>
              </Box>
            </Stack>
          </Stack>

          {/* How much of the caseload is on screen — a filtered list that says
              nothing about what it hid is a list you cannot trust. */}
          <Typography sx={{ mb: 1.5, fontSize: 13, color: palette.text.muted }}>
            {t('gov.dashboard.showing', {
              defaultValue: '{{shown}} of {{total}} files',
              shown: visible.length,
              total: files.length,
            })}
          </Typography>

          <BulkActionBar selected={selectedFiles} onClear={clearSelection} />

          {smartList && listQuery.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {smartList && listQuery.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('gov.smartLists.loadError')}
            </Alert>
          )}

          {visible.length === 0 ? (
            <HairlineCard sx={{ py: 6, px: 3, textAlign: 'center' }}>
              <Typography sx={{ color: palette.text.secondary }}>
                {t('gov.dashboard.noMatches', {
                  defaultValue: 'No files match your search or filter.',
                })}
              </Typography>
            </HairlineCard>
          ) : (
            <>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <FilesTable
                  files={pageRows}
                  sort={sort}
                  onSort={toggleSort}
                  onOpen={(id) => navigate(`/files/${id}`)}
                  selected={selectedIds}
                  onToggle={toggle}
                  onToggleAll={toggleAll}
                />
              </Box>
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <FilesList
                  files={pageRows}
                  selecting={selecting}
                  selected={selectedIds}
                  onToggle={toggle}
                />
              </Box>

              {/* One pager for both layouts: the phone list and the desktop
                  table are two renderings of the same page of the caseload, so
                  paging them separately would report two different positions in
                  one list. */}
              {pageCount > 1 && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 2, alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography sx={{ fontSize: 13, color: palette.text.muted }}>
                    {t('common.table.pageOf', { page: safePage + 1, pages: pageCount })}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      aria-label={t('common.pagination.previous')}
                      disabled={safePage === 0}
                      onClick={() => setPage((n) => Math.max(0, n - 1))}
                    >
                      <Icons.ChevronLeft size={iconSize.md} />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={t('common.pagination.next')}
                      disabled={safePage >= pageCount - 1}
                      onClick={() => setPage((n) => Math.min(pageCount - 1, n + 1))}
                    >
                      <Icons.ChevronRight size={iconSize.md} />
                    </IconButton>
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </>
      )}
      <SaveSearchDialog
        open={saveOpen || !!renaming}
        onClose={() => {
          setSaveOpen(false);
          setRenaming(null);
        }}
        filters={savedFilters}
        editing={renaming}
        onSaved={(search) => {
          if (!renaming) {
            setSmartList(`saved:${search.id}`);
            clearFilters();
          }
        }}
      />
    </AppShell>
  );
}
